import React, {useEffect, useRef, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {useSearchType} from '../../../../../app/hooks/search/useSearchType'
import {useDebouncedValue} from '../../../../../app/hooks/search/useDebouncedValue'
import {useGlobalSearch} from '../../../../../app/hooks/search/useGlobalSearch'
import {useSearchableMembersContext} from '../../../../../app/context/SearchableMembersContext'
import {useUserProfileContext} from '../../../../../app/context/UserProfileContext'
import {ResultItem, SearchType} from '../../../../../app/pages/search/types'
import {buildResultsUrl} from '../../../../../app/pages/search/routing'
import {resultItemPath} from '../../../../../app/pages/search/navigation'
import {
  buildDropdownGroups,
  flattenGroupRows,
  MIN_QUERY_LENGTH,
  nextActiveIndex,
  totalResults,
} from '../../../../../app/pages/search/utils'
import {placeholderForType} from './searchTypeMeta'
import {TypeSelector} from './TypeSelector'
import {SearchInput} from './SearchInput'
import {SubmitButton} from './SubmitButton'
import {ResultsDropdown} from './ResultsDropdown'

const LISTBOX_ID = 'nb-gs-listbox'

/**
 * GS-01 + GS-02 — the header global search: type selector + input + magnifier, with a grouped
 * results dropdown. Combobox a11y: the input owns focus; rows are driven via aria-activedescendant
 * (arrow keys), Enter selects the active row (or submits), Escape closes and keeps focus on the
 * input. A polite live region announces the loading / result-count state.
 */
export const GlobalSearch: React.FC = () => {
  const history = useHistory()
  const {type, setType} = useSearchType()
  const {setSearchParams: setMembersSearch} = useSearchableMembersContext()
  const {setSearchParams: setUserProfile} = useUserProfileContext()

  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const debounced = useDebouncedValue(query, 300)
  const {data, isFetching} = useGlobalSearch(debounced, type)

  const groups = buildDropdownGroups(data)
  const flatRows = flattenGroupRows(groups)
  const totalCount = totalResults(data)
  const queryLongEnough = debounced.trim().length >= MIN_QUERY_LENGTH
  const showDropdown = open && queryLongEnough && data !== null

  // Reset the active row whenever the query/type changes or the dropdown closes.
  useEffect(() => {
    setActiveIndex(-1)
  }, [debounced, type])
  useEffect(() => {
    if (!showDropdown) setActiveIndex(-1)
  }, [showDropdown])

  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  const changeQuery = (value: string) => {
    setQuery(value)
    setOpen(value.trim().length >= MIN_QUERY_LENGTH)
  }

  const goToResults = (targetType: SearchType) => {
    const q = query.trim()
    // Only prime the members context when the target is members / all — otherwise we would
    // pollute the members list state with an unrelated query (e.g. searching experiences).
    if (targetType === 'members' || targetType === 'all') {
      setMembersSearch({where: '', keywords: q, category: '', query: q, type: targetType})
    }
    history.push(buildResultsUrl(q, targetType))
    setOpen(false)
  }

  const submit = () => {
    // Search is disabled
    return
  }

  // Footer "See all results →": when the selector scope is a specific type, drop the query so
  // the destination page shows the full (unfiltered) listing of that type. When scope=All, keep
  // the query and go to the aggregated /search view.
  const seeAllFooter = () => {
    if (type === 'all') {
      goToResults('all')
      return
    }
    if (type === 'members') {
      setMembersSearch({where: '', keywords: '', category: '', query: '', type})
    }
    history.push(buildResultsUrl('', type))
    setOpen(false)
  }

  /** Row click → the entity's own page (D2(a), shared helper). Then dismiss + clear the input. */
  const selectRow = (item: ResultItem) => {
    const nav = resultItemPath(item)
    if (nav.userSelected) setUserProfile({userSelected: nav.userSelected})
    history.push(nav.path)
    setOpen(false)
    setQuery('')
  }

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false)
      inputRef.current?.focus() // keep focus on the input; never strand it
      e.preventDefault()
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
      if (!queryLongEnough || data === null) return
      if (!open) setOpen(true)
      setActiveIndex((i) => nextActiveIndex(i, e.key, flatRows.length))
      e.preventDefault()
      return
    }
    if (e.key === 'Enter' && activeIndex >= 0 && flatRows[activeIndex]) {
      // Select the active row instead of submitting; preventDefault so SearchInput skips onSubmit.
      selectRow(flatRows[activeIndex])
      e.preventDefault()
    }
  }

  const announcement = isFetching
    ? 'Searching…'
    : data
    ? `${totalCount} result${totalCount === 1 ? '' : 's'} found`
    : ''

  return (
    <div className='nb-gs' ref={rootRef}>
      <div className='nb-gs__bar' style={{backgroundColor: '#fafafa', cursor: 'not-allowed', opacity: 0.7}}>
        <TypeSelector value={type} onChange={setType} disabled={true} />
        <SearchInput
          ref={inputRef}
          value={query}
          placeholder={placeholderForType(type)}
          onChange={changeQuery}
          onSubmit={submit}
          onKeyDown={onInputKeyDown}
          onFocus={() => {
            if (queryLongEnough) setOpen(true)
          }}
          expanded={showDropdown}
          controlsId={LISTBOX_ID}
          activeDescendantId={activeIndex >= 0 ? `${LISTBOX_ID}-opt-${activeIndex}` : undefined}
          busy={isFetching}
          disabled={true}
        />
        <SubmitButton onClick={submit} isFetching={isFetching} disabled={true} />
      </div>

      {showDropdown && (
        <ResultsDropdown
          groups={groups}
          query={debounced.trim()}
          totalCount={totalCount}
          activeIndex={activeIndex}
          listboxId={LISTBOX_ID}
          onSelectRow={selectRow}
          onSeeAll={goToResults}
          onSeeAllFooter={seeAllFooter}
        />
      )}

      <span className='nb-gs__sr-only' role='status' aria-live='polite'>
        {announcement}
      </span>
    </div>
  )
}
